import React, { useEffect, useState } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { Box, CircularProgress } from '@material-ui/core';
import i18n from 'i18n-js';

import { ReservationDTO, Reservations } from 'api/controllers/reservations';
import {
    formatGuestName,
    formValuesToModel,
    getDatePart,
    ReservationFormValues,
    reservationToFormValues,
    validateReservationForm,
} from 'helpers/reservationHelper';
import { useAppSelector } from 'index';
import globalStore from 'store/globalStore';
import reservationsStore from 'store/reservationsStore';
import ReservationEditor from './components/ReservationEditor';
import { RESERVATIONS_PATH, useReservationsSubpageGuard, useThunkDispatch } from './hooks';
import { ACTIVE_TAB_STATE } from './index';

export default function EditReservationPage() {
    const { id } = useParams<{ id: string }>();
    const history = useHistory();
    const dispatch = useThunkDispatch();
    const isModuleDisabled = useReservationsSubpageGuard();
    const restaurantId = useAppSelector((state) => state.user.currentRestaurantId);
    const [reservation, setReservation] = useState<ReservationDTO | null>(null);

    const backToActive = () => history.replace(RESERVATIONS_PATH, ACTIVE_TAB_STATE);

    useEffect(() => {
        let active = true;
        setReservation(null);
        if (!isModuleDisabled) {
            Reservations.getById(id)
                .then((current) => {
                    if (!active) return;
                    if (current.status !== 'Confirmed' || current.isArchived) {
                        backToActive();
                    } else {
                        setReservation(current);
                    }
                })
                .catch((error) => {
                    if (active) {
                        dispatch(globalStore.actionCreators.showToaster('error', error));
                        backToActive();
                    }
                });
        }
        return () => {
            active = false;
        };
    }, [id, restaurantId, isModuleDisabled, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSave = async (values: ReservationFormValues): Promise<string | null> => {
        try {
            const result = await dispatch(
                reservationsStore.actionCreators.updateConfirmed(id, formValuesToModel(values))
            );
            if (result.status === 'tableTaken') return result.tableId;
            if (result.status === 'alreadyHandled') {
                dispatch(
                    globalStore.actionCreators.showToaster(
                        'error',
                        i18n.t('reservations.alreadyHandled')
                    )
                );
                backToActive();
                return null;
            }
            dispatch(
                globalStore.actionCreators.showToaster(
                    'success',
                    i18n.t('messages.updatedSuccesfully')
                )
            );
            history.replace(RESERVATIONS_PATH, {
                ...ACTIVE_TAB_STATE,
                date: getDatePart(result.reservation.reservationStartDateTime),
            });
        } catch (error) {
            dispatch(globalStore.actionCreators.showToaster('error', error));
        }
        return null;
    };

    if (isModuleDisabled) return <Redirect to={RESERVATIONS_PATH} />;
    if (!reservation) {
        return (
            <Box p={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <ReservationEditor
            key={reservation.id}
            title={i18n
                .t('reservations.editReservationTitle', {
                    name: formatGuestName(reservation),
                })
                .toUpperCase()}
            channelLine={null}
            reservationId={reservation.id}
            initialValues={reservationToFormValues(reservation)}
            validate={validateReservationForm}
            confirmLabel={i18n.t('button.save')}
            onCancel={backToActive}
            onConfirm={handleSave}
        />
    );
}
